/**
 * Report Type Registry — defines all available report types.
 * Static registry: report types are code-level concerns (prompts, schemas, sections).
 */

export type ReportTypeId =
  | 'feasibility'
  | 'environmental'
  | 'test-fit'
  | 'power-availability'
  | 'water-resource'
  | 'interconnection'
  | 'supply-chain-risk'
  | 'custom-brief'
  | 'financial'
  | 'executive-summary'
  // PSR-1 (row 0.99290) — Public Sentiment Report. Phase 2 of the Public
  // Sentiment work; Phase 1 (PSE-1+2) registers the SED publicSentiment
  // section and its commercial-source fallback. The PSR type carries
  // `usesScoring: false` because it emits a SENTIMENT INDEX 0-100 verdict
  // (computed in PSR-2), not a deterministic feasibility-style category
  // score.
  | 'public-sentiment';

/**
 * `retired` (two-stage Phase 3 part B, 2026-09-07): the type's id stays in the
 * tuple and EVERY persisted report of it stays readable and exportable, but
 * no new one can be generated (the P2.2 dispatch gate refuses), the wizard
 * does not offer it, and it is out of the required set. No `ReportTypeId` is
 * ever renamed or removed — persisted `reportType` is read verbatim.
 */
export type ReportTypeStatus = 'active' | 'coming_soon' | 'beta' | 'retired';

/**
 * TWO-STAGE DEVELOPER DECISION PIPELINE (owner decision 2, 2026-09-06).
 * Stage 1 — Early screening: low-cost diligence that finds a site's
 * showstoppers before any equipment is chosen (the Early Screening
 * Assessment; the custom brief rides along unscored). The retired
 * water-resource type keeps its Stage 1 label for historical reports; its
 * screen now lives in feasibility.
 * Stage 2 — Mature validation: engineering-grade work that needs a PROMOTED
 * generation scenario (Generation & Impact, environmental, financial, supply
 * chain, test fit; interconnection is advisory and unscored).
 * No stage — an ADDITIONAL analysis outside both stages' required sets,
 * results and the screening score (public sentiment), and the executive
 * summary rollup.
 *
 * Owner, 2026-09-30: environmental moved Stage 1 → Stage 2 ("it requires
 * equipment": its air-emissions, noise and generation-footprint findings are
 * assessed against the promoted generation scenario, which the report
 * already freezes — `GENERATION_IMPACT_REPORT_TYPES`); public sentiment moved
 * out of Stage 1 into the Analyses tab's "Additional analyses".
 *
 * The stage labels and groups, and it is also read by behaviour: the Stage 2
 * lock (`stageLockFor`), the two-stage decision hold and the Stage-1-only
 * screening score (`compositeScorer`), and stage-aware next actions. The
 * required-for-GO set (`mandatoryReports`) does not read it.
 */
export type DecisionStage = 1 | 2;

export const DECISION_STAGE_LABEL: Readonly<Record<DecisionStage, string>> = {
  1: 'Stage 1 · Early screening',
  2: 'Stage 2 · Mature validation',
};

export interface ReportTypeDefinition {
  id: ReportTypeId;
  label: string;
  shortLabel: string;
  description: string;
  icon: string; // Lucide icon name
  status: ReportTypeStatus;
  /**
   * Two-stage pipeline membership. None for the executive summary (a rollup)
   * and for an additional analysis outside both stages (public sentiment).
   */
  stage?: DecisionStage;
  expectedDate?: string;
  accentColor: string;
  requiredSedSections: string[];
  promptTemplateId: string;
  /** Pointer to the preference schema label for this type. Same string as id for now. */
  preferenceSchemaId: ReportTypeId;
}

export const REPORT_TYPE_REGISTRY: Record<ReportTypeId, ReportTypeDefinition> = {
  feasibility: {
    id: 'feasibility',
    stage: 1,
    // AP5 — drop the internal "SiteRAG" product code-name from all
    // client-facing surfaces. The label propagates into the report
    // type picker, the document checklist heading, the library index
    // markdown sections, and several admin/test-fixture surfaces, so
    // changing it here is the single source of truth.
    // D3 (two-stage, 2026-09-07): DISPLAY name only — the id stays `feasibility`.
    // The report is Stage 1's screening assessment: it carries the site's
    // showstoppers, the grid case and (Phase 3 part B) the water screen.
    label: 'Early Screening Assessment',
    shortLabel: 'Screening Assessment',
    description: 'Start with a location-based review of evidenced constraints, opportunities and missing information. Add documents, a selected study area and declared project intent to refine power, cooling, water, environmental and regulatory findings. Capacity, layout and economics remain unresolved where their required inputs are missing; engineering and financial validation follow as the project develops.',
    icon: 'FileText',
    status: 'active',
    accentColor: 'green',
    // P3-9 — superset of the strict canonical `REPORT_TYPE_SED_REQUIREMENTS`
    // (SSOT). Added `parcel`, `electricity`, `technology` so the loose registry
    // list and the strict freshness-gate list no longer diverge.
    requiredSedSections: ['climate', 'waterStress', 'seismic', 'grid', 'flood', 'environmental', 'connectivity', 'regulatory', 'nuclearInfra', 'siteResearch', 'parcel', 'electricity', 'technology'],
    promptTemplateId: 'feasibility-v1',
    preferenceSchemaId: 'feasibility',
  },
  environmental: {
    id: 'environmental',
    // Owner, 2026-09-30: Stage 2 · Mature validation — "it requires
    // equipment". Locked with the other Stage 2 analyses until a generation
    // scenario is promoted (`stageLockFor`); a declared grid-only site has no
    // scenario to wait for and runs it unlocked. Still required for every site
    // (`ALWAYS_REQUIRED_FOR_GO`).
    stage: 2,
    label: 'Environmental Impact Assessment',
    shortLabel: 'Environmental',
    description: 'Environmental clearance pathway for a behind-the-meter build sized to the firm 24/7 load: NEPA/CEQA screening, air-quality attainment and emissions headroom for on-site firm generation (recip/gas/SMR nameplate), wetlands, endangered-species habitat, water resources, cultural resources, and per-phase mitigation pathway as the generation build steps up.',
    icon: 'Leaf',
    status: 'active',
    accentColor: 'emerald',
    // P3-9 — superset of strict SSOT: added `regulatory`, `parcel`, `technology`.
    // QA 2026-06 — added `seismic`: the environmental schema requires a
    // seismicImpact narrative, so the USGS seismic section is a true input.
    requiredSedSections: ['environmental', 'flood', 'waterStress', 'climate', 'seismic', 'airQuality', 'siteResearch', 'regulatory', 'parcel', 'technology'],
    promptTemplateId: 'environmental-v1',
    preferenceSchemaId: 'environmental',
  },
  'test-fit': {
    id: 'test-fit',
    stage: 2,
    label: 'Test Fit / Site Layout Analysis',
    shortLabel: 'Test Fit',
    description: 'Site-layout fit for a behind-the-meter campus: data-hall placement plus the on-site firm-generation pad, fuel/BESS yard, and substation footprint sized to the load→nameplate build. Checks setbacks, parking, stormwater detention, and hall orientation for prevailing winds and generator-set acoustics/exhaust.',
    icon: 'Building2',
    status: 'active',
    accentColor: 'blue',
    // P3-9 — superset of strict SSOT: added `regulatory`.
    requiredSedSections: ['climate', 'flood', 'seismic', 'siteResearch', 'parcel', 'regulatory'],
    promptTemplateId: 'test-fit-v1',
    preferenceSchemaId: 'test-fit',
  },
  'power-availability': {
    id: 'power-availability',
    stage: 2,
    // STAGE 5.2 — DISPLAY name only. The id stays `power-availability`:
    // `typedPreferencesAtGen` is power-availability-keyed BY DESIGN and five
    // unrelated report types read it for frozen-strategy currency, the id is
    // carried in the Python agent schema, and no ReportTypeId has ever been
    // renamed (there is no migration or alias map — persisted `reportType` is
    // read verbatim). Same guardrail `executive-summary` and `interconnection`
    // already document.
    //
    // The name states the report's source, which is what makes its gate
    // self-explaining: it is available once a generation scenario is promoted.
    // D3 (two-stage, 2026-09-07): "Validation" — it is Stage 2's mature
    // validation of the promoted scenario, not a screening report.
    label: 'Generation & Impact Validation',
    shortLabel: 'Generation',
    description: 'Firm-capacity availability for the behind-the-meter load — grades each source on ELCC (effective load-carrying capability), not raw capacity factor, against the declared 24/7 MW plus planning reserve. Sizes the load→nameplate build per source, compares grid-import firmness vs on-site firm generation, and weighs interconnection queue, curtailment, and tariff economics per phase.',
    icon: 'Zap',
    status: 'active',
    accentColor: 'amber',
    // P3-9 — superset of strict SSOT: added `regulatory`.
    requiredSedSections: ['grid', 'electricity', 'solar', 'wind', 'nuclearInfra', 'siteResearch', 'regulatory'],
    promptTemplateId: 'power-availability-v1',
    preferenceSchemaId: 'power-availability',
  },
  'water-resource': {
    id: 'water-resource',
    stage: 1,
    label: 'Water Resource Assessment',
    shortLabel: 'Water',
    description: 'Water adequacy for a behind-the-meter campus where both the firm 24/7 IT load and the on-site firm generation draw cooling water. Sizes demand off the load→nameplate build (cooling for the data halls plus thermal rejection from recip/gas/SMR generation), then tests groundwater availability, municipal supply capacity, water rights, and drought resilience per phase.',
    icon: 'Droplets',
    // RETIRED (two-stage Phase 3 part B, 2026-09-07; owner decisions D1/D3):
    // the water SCREEN — supply availability, groundwater & surface water,
    // water rights, drought resilience — now lives in the feasibility report
    // (Early Screening Assessment, `waterAnalysis`), and the scenario's water
    // DUTY on Generation & Impact (P3.2). Existing Water Resource reports stay
    // readable; new ones are refused at the dispatch gate.
    status: 'retired',
    accentColor: 'cyan',
    // P3-9 — superset of strict SSOT: added `environmental`, `parcel`, `regulatory`.
    requiredSedSections: ['waterStress', 'climate', 'flood', 'siteResearch', 'environmental', 'parcel', 'regulatory'],
    promptTemplateId: 'water-resource-v1',
    preferenceSchemaId: 'water-resource',
  },
  interconnection: {
    id: 'interconnection',
    stage: 2,
    label: 'Connectivity Feasibility Study',
    shortLabel: 'Connectivity',
    description: 'Network connectivity posture for a behind-the-meter data center — fiber route diversity, latency modeling, carrier availability, and submarine-cable proximity. Frames redundancy for island-mode vs grid-primary operation of the on-site firm power build, so connectivity resilience is matched to the same uptime the firm-capacity plant is sized to deliver.',
    icon: 'Cable',
    status: 'active',
    accentColor: 'purple',
    // P3-9 — superset of strict SSOT: added `regulatory`, `parcel`.
    requiredSedSections: ['connectivity', 'grid', 'siteResearch', 'regulatory', 'parcel'],
    promptTemplateId: 'interconnection-v1',
    preferenceSchemaId: 'interconnection',
  },
  'supply-chain-risk': {
    id: 'supply-chain-risk',
    stage: 2,
    label: 'Supply Chain Risk Report',
    shortLabel: 'Supply Chain',
    description: 'Supply-chain risk for the behind-the-meter generation build — supplier, equipment, lead-time, and CAPEX exposure for the firm-capacity assets (turbines/recip/SMR, BESS, switchgear) sized to the load→nameplate plan. Derived from BoM plans, quote signals, and per-phase timeline assumptions so long-lead firm-power equipment is flagged against the phase it must energize.',
    icon: 'FileText',
    status: 'active',
    accentColor: 'amber',
    // P3-9 — superset of strict SSOT: added `technology`.
    requiredSedSections: ['siteResearch', 'grid', 'regulatory', 'technology'],
    promptTemplateId: 'supply-chain-risk-v1',
    // P3-5 — own preference schema id (was reusing 'feasibility'). The
    // supply-chain report is a PROCUREMENT diligence, not a site-feasibility
    // score, so it carries its own preference surface (`supplyChainSetup()` in
    // reportSetupRequirements) rather than the feasibility KPI-weight knob.
    preferenceSchemaId: 'supply-chain-risk',
  },
  // Public Sentiment surfaces the per-BTM stakeholder sentiment map, theme
  // deep-dives, trend analysis, comparable-site benchmark, and a mitigation
  // playbook as a standalone deliverable. The `usesScoring: false` decision
  // lives in the per-type
  // ReportPromptConfig (see getPublicSentimentConfig) because PSR verdict
  // is a sentiment index, not a deterministic feasibility-style category
  // score.
  'public-sentiment': {
    id: 'public-sentiment',
    // Owner, 2026-09-30: an ADDITIONAL analysis — no stage. It is never in the
    // required-for-GO set, so it neither completes nor holds either stage, and
    // the social pillar it feeds is outside the Stage-1 screening score.
    // Existing reports stay readable and are listed under the Analyses tab's
    // "Additional analyses".
    label: 'Public Sentiment Report',
    shortLabel: 'Public Sentiment',
    description: 'Stakeholder sentiment toward a behind-the-meter data center and its on-site firm power — stakeholder map, per-BTM sentiment, theme deep-dives (noise, emissions, water, fuel logistics of the firm-generation build), trend analysis, comparable-site benchmark, and a per-phase mitigation playbook drawn from public records and news coverage.',
    icon: 'MessageSquare',
    // Available to every tenant; still mutable through the AdminPanel
    // operational override when a report type must be held globally.
    status: 'active',
    accentColor: 'rose',
    // The primary input is the SED `publicSentiment` section (PSE-1) — once
    // PSE-1 lands the section ID will exist in SED_SECTION_IDS. The
    // `siteContext` token is the user-declared stance narrative.
    // `requiredSedSections: string[]` is intentionally loose-typed in
    // `ReportTypeDefinition`, so this list is decoupled from the strict
    // `SedSectionId` union used elsewhere; the strict-typed maps
    // (`REPORT_TYPE_SED_REQUIREMENTS`) fall back to existing sections
    // until PSE-1 publishes `publicSentiment`.
    requiredSedSections: ['publicSentiment', 'siteContext'],
    promptTemplateId: 'public-sentiment-v1',
    preferenceSchemaId: 'public-sentiment',
  },
  'custom-brief': {
    id: 'custom-brief',
    stage: 1,
    label: 'Custom Brief',
    shortLabel: 'Custom Brief',
    description: 'User-defined diligence brief for a behind-the-meter project — answer your own ordered questions against site evidence, the firm-capacity / load→nameplate power basis, DAM context, findings, red flags, and confidence.',
    icon: 'ClipboardList',
    status: 'active',
    accentColor: 'purple',
    requiredSedSections: ['grid', 'regulatory', 'siteResearch', 'electricity', 'environmental'],
    promptTemplateId: 'custom-brief-v1',
    preferenceSchemaId: 'custom-brief',
  },
  // FIN-1 (wf w7nuyquwd) — Financial Viability Report. HYBRID type:
  // deterministic-live body (projectFinance.recompute + finDecision/* engines)
  // with an LLM narrative that SHIPS (audit D8 / Q13 — the "OFF-by-default,
  // export only" this comment used to claim was never true: the narrative
  // reaches `report.summary`, the cover standfirst and the Decision-Guide
  // digest). It is now written AGAINST the deterministic verdict, which is
  // injected into its prompt. `usesScoring: true`
  // (see scoringConfig below) drives the deterministic `scoreSiteFinancial`
  // banding into report.overallScore so the verdict tier is data-grounded
  // (Viable / Conditional / Unfinanceable), and a compact
  // `report.financialAnalysis` stamp is persisted on create/recompute so
  // cross-report aggregators (rollup worst-source floor, BTM map, overview
  // cards) can read the headline metrics without re-running the engine.
  financial: {
    id: 'financial',
    stage: 2,
    label: 'Financial Viability Report',
    shortLabel: 'Financial',
    description: 'Investment-grade financial viability for the behind-the-meter build — leads with a deterministic Viable / Conditional / Unfinanceable verdict from the live project-finance engine (LCOE, NPV, IRR vs hurdle, payback) and the Monte-Carlo probability of a positive NPV. Compares pathway economics, decomposes CAPEX/OPEX and per-phase LCOE, runs sensitivity (tornado + scenario flips) and a risk-adjusted WACC, and credits IRA ITC/PTC and PPA structure as financial adjustments — with a deterministic action plan to clear the binding constraints.',
    icon: 'DollarSign',
    status: 'active',
    accentColor: 'violet',
    // Financial diligence is anchored on the power/economics evidence set:
    // grid + electricity + nuclearInfra (firm-generation cost basis), plus
    // siteResearch + regulatory (incentive/tariff context).
    requiredSedSections: ['grid', 'electricity', 'nuclearInfra', 'siteResearch', 'regulatory'],
    promptTemplateId: 'financial-v1',
    preferenceSchemaId: 'financial',
  },
  'executive-summary': {
    // CONCEPT A/B GUARDRAIL: the internal id stays 'executive-summary' (concept
    // A — every route/registry/section/gate keys off it). Only the DISPLAY name
    // (label/shortLabel/description, concept B) changes to the LIVE Go/No-Go
    // Decision Guide wording: this report no longer has a generate step — it
    // auto-aggregates the site's reports + the Go/No-Go verdict + the Path-to-GO
    // steps, recomputed live at render. `promptTemplateId` is retained but unused
    // (the optional LLM exec-summary narrative is export-only / off-by-default).
    id: 'executive-summary',
    label: 'Go/No-Go Decision Guide',
    shortLabel: 'Decision Guide',
    description: 'A LIVE go/no-go decision guide for the behind-the-meter build — no generate step. It auto-aggregates every report the site has produced, recomputes the deterministic GO / CONDITIONAL / NO-GO verdict (composite + firm-capacity ELCC + worst mandatory-source floor) and the Path-to-GO diligence gate at render, and surfaces the binding constraints holding the call back. Always current with the latest evidence, intent, and source reports.',
    icon: 'FileStack',
    status: 'active',
    accentColor: 'indigo',
    requiredSedSections: ['grid', 'siteResearch', 'electricity', 'environmental'],
    promptTemplateId: 'executive-summary-v1',
    preferenceSchemaId: 'executive-summary',
  },
};

/**
 * Runtime status overrides.
 *
 * Source of truth = Firestore (system/reportTypeStatus, see
 * services/reportTypeStatusService.ts). The in-memory cache below is updated
 * three ways:
 *   1. Module init reads localStorage so the app boots with the previous
 *      session's snapshot before Firestore hydrates.
 *   2. After auth, AuthGate calls initReportTypeOverrides() which fetches
 *      from Firestore and replaces the cache (and re-writes localStorage).
 *   3. Admin writes via setReportTypeStatusOverrides update the cache and
 *      mirror to localStorage; the AdminPanel additionally awaits
 *      saveReportTypeOverrides() to push to Firestore.
 *
 * Readers (getReportType, getActiveReportTypes, getAllReportTypes) stay
 * fully synchronous against the cache.
 */
const REPORT_TYPE_OVERRIDES_LOCAL_KEY = 'reportTypeOverrides';
let _statusOverrides: Partial<Record<ReportTypeId, ReportTypeStatus>> = {};

// Load overrides from localStorage on module init (instant baseline, no auth required)
try {
  const saved = typeof localStorage !== 'undefined' ? localStorage.getItem(REPORT_TYPE_OVERRIDES_LOCAL_KEY) : null;
  if (saved) _statusOverrides = JSON.parse(saved);
} catch { /* ignore */ }

/** Update the in-memory cache and the localStorage write-through. Does NOT
 *  touch Firestore — the AdminPanel orchestrates the Firestore write so it
 *  can surface failures. */
export function setReportTypeStatusOverrides(overrides: Partial<Record<ReportTypeId, ReportTypeStatus>>) {
  _statusOverrides = overrides;
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(REPORT_TYPE_OVERRIDES_LOCAL_KEY, JSON.stringify(overrides));
    }
  } catch (e) {
    if (e instanceof Error && e.name === 'QuotaExceededError') console.warn('localStorage quota exceeded');
  }
}

export function getReportTypeStatusOverrides(): Partial<Record<ReportTypeId, ReportTypeStatus>> {
  return _statusOverrides;
}

/**
 * Fetch the latest overrides from Firestore and replace the in-memory cache.
 * Called by AuthGate after sign-in. Best-effort — on failure (offline,
 * permission-denied) the existing localStorage-derived cache stays. */
export async function initReportTypeOverrides(): Promise<void> {
  try {
    const { fetchReportTypeOverrides } = await import('../services/reportTypeStatusService');
    const remote = await fetchReportTypeOverrides();
    setReportTypeStatusOverrides(remote);
  } catch (e) {
    console.warn('initReportTypeOverrides failed; keeping localStorage cache:', e);
  }
}

export function getReportType(id: ReportTypeId): ReportTypeDefinition {
  const base = REPORT_TYPE_REGISTRY[id];
  // Registry retirement is a product migration, not a rollout override. Old
  // cached/Firestore 'active' entries must never resurrect its generation UI.
  if (base.status === 'retired') return base;
  const override = _statusOverrides[id];
  return override ? { ...base, status: override } : base;
}

/**
 * Convenience: returns the required SED section ids for a report type.
 * Centralised so the wizard, preflight, and welcome card all read the
 * same source of truth instead of poking `requiredSedSections` directly
 * on the registry entry.
 *
 * Falls back to `[]` when the id is unknown — callers can render an
 * empty preview without crashing if a feature flag flips a type off.
 */
export function getRequiredSedSectionsFor(id: ReportTypeId): string[] {
  const def = REPORT_TYPE_REGISTRY[id];
  return def ? [...def.requiredSedSections] : [];
}

export function getActiveReportTypes(): ReportTypeDefinition[] {
  return getAllReportTypes().filter(t => t.status === 'active' || t.status === 'beta');
}

/**
 * Report types to OFFER in a user-facing picker (Reports-tab empty state,
 * NewReportWizard step 1) for a given identity.
 *
 * `beta` types are hidden from tenant users and surfaced only to Datacrate
 * Admins. `active` types, including Public Sentiment, are offered to everyone;
 * `coming_soon` is offered to no one (the existing locked card affordance
 * still renders it via `getAllReportTypes()` where applicable).
 *
 * Pure / synchronous against the runtime status cache — pass the caller's
 * Deep-Atomic-admin flag in (the config layer stays free of store/React deps;
 * see `lib/isDatacrateAdmin`).
 */
export function getOfferableReportTypes(isDatacrateAdmin: boolean): ReportTypeDefinition[] {
  return getActiveReportTypes().filter(
    (t) =>
      !isLiveOnlyReportType(t.id)
      && (t.status === 'active' || (t.status === 'beta' && isDatacrateAdmin)),
  );
}

/** Live cockpit surfaces that remain readable as historical artifacts but are
 * not dispatchable as new generated reports. */
export function isLiveOnlyReportType(type: ReportTypeId): boolean {
  return type === 'executive-summary';
}


/** The two-stage pipeline stage of a report type; `null` for the executive
 *  summary rollup and for an additional analysis outside both stages (Public
 *  Sentiment since 2026-09-30). Reads the registry so the wizard, Path to GO
 *  and the applicability resolver agree by construction. */
export function reportTypeStage(id: ReportTypeId): DecisionStage | null {
  return REPORT_TYPE_REGISTRY[id]?.stage ?? null;
}

export function getAllReportTypes(): ReportTypeDefinition[] {
  return Object.values(REPORT_TYPE_REGISTRY).map(t => getReportType(t.id));
}

/**
 * Meta / aggregation report types that do NOT introduce primary diligence
 * requirements of their own — they synthesize / re-package findings from the
 * other types. Excluded from the default coverage scope so a planning-stage
 * site (no reports yet) doesn't get spurious extra checklist rows from these.
 *
 * Kept here (not inlined per-consumer) so every coverage surface excludes the
 * SAME set — see `getCoverageScopeReportTypes`.
 */
export const COVERAGE_META_REPORT_TYPES: readonly ReportTypeId[] = [
  'executive-summary',
  'custom-brief',
];

/**
 * COVERAGE-CHECK CONSISTENCY — single source of truth for "which report
 * types does a coverage / readiness surface evaluate?".
 *
 * Every coverage consumer MUST resolve its report-type scope through this
 * helper so the SET of checklist requirements it walks is identical. The live
 * consumers are the Site-Evidence CoverageMatrix and the Site Evidence tab's
 * ranked DCT matcher that feeds it (so the Tier-3 match set lines up with the
 * matrix rows). Previously each surface hand-rolled its own fallback when the
 * site had no reports yet:
 *   - CoverageMatrix      → `Object.keys(REPORT_TYPE_REGISTRY)` (ALL types,
 *                            including coming-soon + meta aggregation types)
 *   - CoverageSummaryPanel → active+beta types
 *   - Overview heatmap    → active+beta minus the meta aggregation types
 * …which produced DIFFERENT requirement counts (and therefore different
 * "missing" tallies) for the SAME site across the three views. The
 * Documents-tab CoverageSummaryPanel and the Overview heatmap have since been
 * removed as dead UI (2026-09-28).
 *
 * Resolution:
 *   - When `scopeReportTypes` is non-empty (the site has reports), use it
 *     verbatim — the site's own report types are authoritative.
 *   - Otherwise fall back to the active/beta types EXCLUDING the meta
 *     aggregation types (`COVERAGE_META_REPORT_TYPES`). This was the
 *     Overview's deliberate choice and is the most defensible planning-view
 *     default (deprecated/coming-soon types and the two synthesis types add
 *     no primary diligence rows).
 *
 * TWO-STAGE — a caller that knows the site is still in early screening passes
 * `decisionStage: 1`, and the empty-scope fallback then plans only Stage 1
 * requirements (Stage 2 requirements join once screening completes). Callers
 * that do not pass a stage keep the full planning set.
 *
 * Pure / synchronous against the runtime status cache so it's safe inside a
 * `useMemo` selector.
 */
export function getCoverageScopeReportTypes(
  scopeReportTypes: readonly ReportTypeId[],
  options: { decisionStage?: DecisionStage } = {},
): ReportTypeId[] {
  if (scopeReportTypes.length > 0) return scopeReportTypes.filter(id => getReportType(id).status !== 'retired');
  const meta = new Set<ReportTypeId>(COVERAGE_META_REPORT_TYPES);
  return getActiveReportTypes()
    .map(t => t.id)
    .filter(id => !meta.has(id))
    .filter(id => options.decisionStage !== 1 || reportTypeStage(id) === 1);
}
